import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Card, Form, Select, DatePicker, Input, Button, Space, Tag, message, Empty, Spin, Table, Segmented, Tabs,
} from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import LinksFormList, { cleanLinks } from '../components/LinksFormList';
import EvalCommentsPanel from '../components/EvalCommentsPanel';

// Evaluate & Coach > Generate Coaching Recap. Fills a recap template for every
// agent with evaluations on a campaign in a month. Nothing is saved until a
// card's Create: each card is edited here, then made into a draft coaching
// session through the normal create endpoint.
const STATUS_OPTIONS = [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }];

export default function GenerateRecapPage() {
  const currentUser = useContext(UserContext);
  const [form] = Form.useForm();

  const [reqTypes,   setReqTypes]   = useState([]);
  const [campaigns,  setCampaigns]  = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [jobs,       setJobs]       = useState([]);
  const [coaches,    setCoaches]    = useState([]);
  const [generating, setGenerating] = useState(false);
  const [cards,      setCards]      = useState(null);   // null = not generated yet
  // Sessions already made for this recap month: listed, not carded.
  const [already,    setAlready]    = useState([]);
  const [context,    setContext]    = useState(null);   // {campaignId, requestTypeId}
  const [creatingAll, setCreatingAll] = useState(false);
  // Coaches to share the cards between (Assign Evenly).
  const [splitCoaches, setSplitCoaches] = useState([]);
  // All Agents: every card down the page. One at a Time: an agent list on
  // the left and one card beside it, for researching an agent properly.
  const [view,       setView]       = useState('all');
  const [focusId,    setFocusId]    = useState(null);
  // Agents tab: tick the agents to work on. Selected tab: their cards. An
  // analyst coaches only some of the agents, so the rest never get a card.
  const [tab,        setTab]        = useState('agents');

  useEffect(() => {
    api.get('/api/coaching/request-types').then((r) => setReqTypes(r.data || [])).catch(() => {});
    api.get('/api/coaching/campaigns').then((r) => setCampaigns(r.data || [])).catch(() => {});
    api.get('/api/recap-templates', { params: { active_only: 1 } }).then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get('/api/jobs').then((r) => {
      const list = (r.data || []).filter((j) => j.IsActive !== 0);
      setJobs(list);
      const agent = list.find((j) => (j.JobName || '').trim().toLowerCase() === 'agent');
      if (agent) form.setFieldValue('job_ids', [agent.JobID]);
    }).catch(() => {});
    form.setFieldValue('month', dayjs().subtract(1, 'month').startOf('month'));
  }, [form]);

  const loadCoaches = (campaignId) => {
    setCoaches([]);
    setSplitCoaches([]);   // a new campaign has its own coaches
    if (!campaignId) return;
    api.get('/api/coaching/coaches', { params: { campaign_id: campaignId } })
      .then((r) => setCoaches(r.data || [])).catch(() => {});
  };

  const generate = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setGenerating(true);
    try {
      const res = await api.post('/api/coaching-recaps/generate', {
        request_type_id: v.request_type_id,
        month:           v.month.format('YYYY-MM'),
        campaign_id:     v.campaign_id,
        template_id:     v.template_id,
        job_ids:         v.job_ids,
        adhoc_message_1: v.adhoc_message_1 || '',
        adhoc_message_2: v.adhoc_message_2 || '',
      });
      const due = dayjs().add(7, 'day');
      setAlready(res.data.already_created || []);
      setFocusId(null);
      setTab('agents');
      setCards((res.data.agents || []).map((a) => ({
        ...a, coachId: undefined, dueDate: due, status: 'draft', creating: false, createdId: null,
      })));
      setContext({ campaignId: v.campaign_id, requestTypeId: v.request_type_id, month: v.month.format('YYYY-MM'),
                   templateId: v.template_id });
      loadCoaches(v.campaign_id);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to generate recaps.');
    } finally {
      setGenerating(false);
    }
  };

  const updateCard = (userId, patch) =>
    setCards((prev) => prev.map((c) => (c.UserID === userId ? { ...c, ...patch } : c)));

  // The ticked agents: the only ones with cards, and all Create All and
  // Assign Evenly act on.
  const visible = (cards || []).filter((c) => c.selected);
  const setSelected = (ids) => {
    const on = new Set(ids);
    // A created session stays selected; it cannot be taken back from here.
    setCards((prev) => prev.map((c) => ({ ...c, selected: !!c.createdId || on.has(c.UserID) })));
  };

  // Creates one draft session; returns true on success. Errors are shown here.
  // Active needs a due date after today: the session page asks for a new
  // date when activating with today or earlier, and a card cannot.
  const dueTooEarly = (card) => card.status === 'active'
    && (!card.dueDate || !card.dueDate.isAfter(dayjs(), 'day'));

  const createOne = async (card) => {
    if (!card.coachId) { message.warning(`Pick a coach for ${card.AgentName}.`); return false; }
    if (!card.Title?.trim()) { message.warning(`${card.AgentName} needs a title.`); return false; }
    if (dueTooEarly(card)) { message.warning(`${card.AgentName}: pick a due date after today to create as Active.`); return false; }
    updateCard(card.UserID, { creating: true });
    try {
      const res = await api.post('/api/coaching/sessions', {
        title:           card.Title.trim(),
        campaign_id:     context.campaignId,
        request_type_id: context.requestTypeId,
        due_date:        card.dueDate ? card.dueDate.format('YYYY-MM-DD') : null,
        coach_user_id:   card.coachId,
        participant_ids: [card.UserID],
        viewer_ids:      currentUser?.ID ? [currentUser.ID] : [],
        // The last day of the month this recap covers.
        applicable_date: dayjs(`${context.month}-01`).endOf('month').format('YYYY-MM-DD'),
        note:            card.Notes || null,
        urls:            cleanLinks(form.getFieldValue('urls')),
      });
      const sessionId = res.data.ID;
      if (card.Comment?.trim()) {
        try {
          await api.post(`/api/coaching/sessions/${sessionId}/comments`, { comment: card.Comment.trim() });
        } catch {
          message.warning(`Session #${sessionId} was created, but its comment could not be added.`);
        }
      }
      // Created as Draft, then activated through the normal update, so the
      // participant due dates and activation alerts work as on the session page.
      if (card.status === 'active') {
        try {
          await api.put(`/api/coaching/sessions/${sessionId}`, { status: 'active' });
        } catch (err) {
          message.warning(`Session #${sessionId} was created as Draft; it could not be set to Active: ${err.response?.data?.error || 'update failed'}`);
        }
      }
      updateCard(card.UserID, { creating: false, createdId: sessionId });
      return true;
    } catch (err) {
      updateCard(card.UserID, { creating: false });
      message.error(`${card.AgentName}: ${err.response?.data?.error || 'create failed'}`);
      return false;
    }
  };

  const createAll = async () => {
    const waiting = visible.filter((c) => !c.createdId);
    // All or nothing: a card without a coach stops the whole run, so none is
    // left behind by accident. Each card's own Create is there for one at a time.
    if (waiting.some((c) => !c.coachId)) {
      message.error('Not all cards are assigned a coach.');
      return;
    }
    const early = waiting.filter(dueTooEarly);
    if (early.length) {
      message.error(`Active needs a due date after today: ${early.map((c) => c.AgentName).join(', ')}.`);
      return;
    }
    const ready = waiting;
    if (!ready.length) { message.info('No cards are waiting to be created.'); return; }
    setCreatingAll(true);
    let ok = 0;
    for (const c of ready) {
      // One at a time: the alerts and inserts behind each create are not batched.
      // eslint-disable-next-line no-await-in-loop
      if (await createOne(c)) ok += 1;
    }
    setCreatingAll(false);
    message.success(`Created ${ok} of ${ready.length} coaching session${ready.length === 1 ? '' : 's'}.`);
  };

  // Round robin down the cards not yet created: the first coach gets the
  // first agent, the second the second, and so on, wrapping around. Only on
  // the button, so picking coaches never changes a card by itself.
  const assignEvenly = () => {
    if (!splitCoaches.length) { message.warning('Pick one or more coaches first.'); return; }
    let i = 0;
    const shown = new Set(visible.map((c) => c.UserID));
    setCards((prev) => prev.map((c) => {
      if (c.createdId || !shown.has(c.UserID)) return c;
      const coachId = splitCoaches[i % splitCoaches.length];
      i += 1;
      return { ...c, coachId };
    }));
  };

  // Prototype: the in-house model rewrites the card's Notes (the template's
  // recap) with the comments panel's material. Shown in its own box to copy
  // from; the card's Notes are never changed.
  const draftWithAI = async (card) => {
    updateCard(card.UserID, { aiLoading: true });
    try {
      const res = await api.post('/api/coaching-recaps/ai-draft', {
        user_id: card.UserID, campaign_id: context.campaignId, month: context.month, agent_name: card.AgentName,
        template_id: context.templateId,
        notes: card.Notes || '',
      });
      updateCard(card.UserID, { aiLoading: false, aiDraft: res.data.draft });
    } catch (err) {
      updateCard(card.UserID, { aiLoading: false });
      message.error(`${card.AgentName}: ${err.response?.data?.error || 'AI draft failed'}`);
    }
  };

  const coachOptions = coaches.map((c) => ({ value: c.ID, label: c.DisplayName }));
  const pending      = visible.filter((c) => !c.createdId);
  const focusIdx     = Math.max(0, visible.findIndex((c) => c.UserID === focusId));
  const focused      = visible[focusIdx];
  const coachName    = (id) => coaches.find((x) => x.ID === id)?.DisplayName;

  const renderCard = (c) => (
    <Card
      key={c.UserID}
      size="small"
      title={
        <Space wrap>
          <span>{c.AgentName}</span>
          {c.TeamName && <Tag>{c.TeamName}</Tag>}
          <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
            {c.EvalCount} eval{c.EvalCount === 1 ? '' : 's'}
            {c.AvgQAScore != null && ` · ${Number(c.AvgQAScore).toFixed(1)}%`}
          </Typography.Text>
        </Space>
      }
      extra={c.createdId
        ? <Link to={`/coaching/sessions/${c.createdId}`}><Tag color="green">Created #{c.createdId}</Tag></Link>
        // Remove unticks the agent; what was typed on the card is kept if
        // they are ticked again.
        : <Button size="small" onClick={() => updateCard(c.UserID, { selected: false })}>Remove</Button>}
      // Once created, the card collapses to its header (name, team
      // and the Created link); there is nothing left to edit.
      styles={c.createdId ? { body: { display: 'none' } } : undefined}
    >
      {!c.createdId && (<>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Input
          value={c.Title}
          maxLength={255}
          disabled={!!c.createdId}
          onChange={(e) => updateCard(c.UserID, { Title: e.target.value })}
          addonBefore="Title"
        />
        {/* Reference only: what evaluators wrote this month. */}
        <EvalCommentsPanel userId={c.UserID} campaignId={context.campaignId} month={context.month} />
        <div>
          <Button size="small" onClick={() => draftWithAI(c)} loading={!!c.aiLoading}>
            {c.aiDraft ? 'Redraft with AI' : 'Draft with AI'}
          </Button>
          <Tag style={{ marginLeft: 8 }}>Prototype</Tag>
        </div>
        {/* With an AI draft: Notes on the left, the draft beside it to compare
            (stacking again on a narrow screen). Without one, Notes alone. */}
        <div style={{ display: 'grid', gap: 12, alignItems: 'start',
          gridTemplateColumns: c.aiDraft ? 'repeat(auto-fit, minmax(360px, 1fr))' : '1fr' }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Notes</Typography.Text>
            <Input.TextArea
              value={c.Notes}
              autoSize={{ minRows: 4, maxRows: 16 }}
              disabled={!!c.createdId}
              onChange={(e) => updateCard(c.UserID, { Notes: e.target.value })}
            />
          </div>
          {c.aiDraft && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>AI recap note (review before copying into Notes)</Typography.Text>
              <div style={{ background: '#f6f8fa', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginBottom: 6 }}>{c.aiDraft}</div>
                <Space>
                  {/* Replaces the Notes with the draft; the draft stays here to compare. */}
                  <Button size="small" type="primary" disabled={!!c.createdId}
                    onClick={() => { updateCard(c.UserID, { Notes: c.aiDraft }); message.success('Notes replaced with the AI draft.'); }}>
                    Use This
                  </Button>
                  <Button size="small" onClick={() => navigator.clipboard?.writeText(c.aiDraft).then(() => message.success('Copied.'))}>
                    Copy
                  </Button>
                  <Button size="small" type="text" onClick={() => updateCard(c.UserID, { aiDraft: null })}>
                    Dismiss
                  </Button>
                </Space>
              </div>
            </div>
          )}
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Comment</Typography.Text>
          <Input.TextArea
            value={c.Comment}
            autoSize={{ minRows: 2, maxRows: 10 }}
            disabled={!!c.createdId}
            onChange={(e) => updateCard(c.UserID, { Comment: e.target.value })}
          />
        </div>
        <Space wrap align="end">
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Coach</Typography.Text>
            <Select
              style={{ width: 220 }}
              placeholder="Coach"
              showSearch
              optionFilterProp="label"
              options={coachOptions}
              value={c.coachId}
              disabled={!!c.createdId}
              onChange={(id) => updateCard(c.UserID, { coachId: id })}
            />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Due Date</Typography.Text>
            <DatePicker
              value={c.dueDate}
              format="YYYY-MM-DD"
              placeholder="Due Date"
              disabled={!!c.createdId}
              onChange={(d) => updateCard(c.UserID, { dueDate: d })}
            />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Status</Typography.Text>
            <Select
              style={{ width: 110 }}
              value={c.status}
              disabled={!!c.createdId}
              onChange={(v) => updateCard(c.UserID, { status: v })}
              options={STATUS_OPTIONS}
            />
          </div>
          <Button type="primary" loading={c.creating} disabled={!!c.createdId || creatingAll}
                  onClick={() => createOne(c)}>
            Create
          </Button>
        </Space>
      </Space>
      </>)}
    </Card>
  );

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Generate Coaching Recap</Typography.Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', columnGap: 16 }}>
            <Form.Item label="Coaching Request Type" name="request_type_id" rules={[{ required: true, message: 'Required' }]}>
              <Select showSearch optionFilterProp="label" options={reqTypes.map((t) => ({ value: t.ID, label: t.Name }))} />
            </Form.Item>
            <Form.Item label="Year-Month" name="month" rules={[{ required: true, message: 'Required' }]}>
              <DatePicker picker="month" format="YYYY-MM" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Campaign" name="campaign_id" rules={[{ required: true, message: 'Required' }]}>
              <Select showSearch optionFilterProp="label" options={campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
            </Form.Item>
            {/* Two columns wide: template names are long. */}
            <Form.Item label="Recap Template" name="template_id" rules={[{ required: true, message: 'Required' }]}
              style={{ gridColumn: 'span 2' }}>
              <Select
                showSearch
                optionFilterProp="label"
                options={templates.map((t) => ({ value: t.ID, label: t.Name }))}
                // Picking a template fills Links with its defaults; they can be
                // changed here before creating.
                onChange={(id) => {
                  const t = templates.find((x) => x.ID === id);
                  form.setFieldValue('urls', (t?.DefaultLinks || []).map((u) => ({ url: u.url, label: u.label || '' })));
                }}
              />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', columnGap: 16 }}>
            <Form.Item label="Job(s)" name="job_ids" rules={[{ required: true, message: 'Required' }]}>
              <Select mode="multiple" optionFilterProp="label" options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', columnGap: 16 }}>
            <Form.Item label="Ad-hoc Message 1" name="adhoc_message_1">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item label="Ad-hoc Message 2" name="adhoc_message_2">
              <Input.TextArea rows={2} />
            </Form.Item>
          </div>
          <Form.Item label="Links (added to every coaching session created)">
            <LinksFormList name="urls" />
          </Form.Item>
          <Button type="primary" onClick={generate} loading={generating}>Generate</Button>
        </Form>
      </Card>

      <Spin spinning={generating}>
        {already.length > 0 && (
          <Card size="small" title={`Already created sessions (${already.length})`} style={{ marginBottom: 16 }}>
            <Table
              rowKey={(r) => `${r.SessionID}-${r.UserID}`}
              size="small"
              dataSource={already}
              pagination={false}
              columns={[
                { title: 'Coaching Session ID', dataIndex: 'SessionID', key: 'SessionID', width: 160,
                  render: (v) => <Link to={`/coaching/sessions/${v}`}>#{v}</Link> },
                { title: 'Request Type', dataIndex: 'RequestType', key: 'RequestType' },
                { title: 'Agent Name', dataIndex: 'AgentName', key: 'AgentName' },
                { title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
                  render: (v) => <Tag>{v ? v.charAt(0).toUpperCase() + v.slice(1) : '—'}</Tag> },
                { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', render: (v) => v || '—' },
              ]}
            />
          </Card>
        )}

        {cards && cards.length === 0 && already.length === 0 && (
          <Empty description="No agents with evaluations for that campaign, month and job." />
        )}

        {cards && cards.length > 0 && (
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: 'agents',
                label: `Agents (${cards.length})`,
                children: (
                  <Table
                    rowKey="UserID"
                    size="small"
                    dataSource={cards}
                    pagination={false}
                    rowSelection={{
                      selectedRowKeys: visible.map((c) => c.UserID),
                      onChange: setSelected,
                      getCheckboxProps: (c) => ({ disabled: !!c.createdId }),
                    }}
                    columns={[
                      { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName',
                        sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || '') },
                      { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', render: (v) => v || '—' },
                      { title: 'Evals', dataIndex: 'EvalCount', key: 'EvalCount', width: 90, align: 'right',
                        sorter: (a, b) => (a.EvalCount || 0) - (b.EvalCount || 0) },
                      { title: 'Avg QA Score', dataIndex: 'AvgQAScore', key: 'AvgQAScore', width: 130, align: 'right',
                        sorter: (a, b) => (a.AvgQAScore ?? -1) - (b.AvgQAScore ?? -1),
                        render: (v) => (v != null ? `${Number(v).toFixed(1)}%` : '—') },
                      { title: 'Session', key: 'Session', width: 130,
                        render: (_, c) => (c.createdId
                          ? <Link to={`/coaching/sessions/${c.createdId}`}><Tag color="green">Created #{c.createdId}</Tag></Link>
                          : null) },
                    ]}
                  />
                ),
              },
              {
                key: 'selected',
                label: `Selected (${visible.length})`,
                children: (
                  <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <Space wrap>
                      <Typography.Text>Coaches:</Typography.Text>
                      <Select
                        mode="multiple"
                        style={{ minWidth: 280 }}
                        placeholder="Pick one or more coaches"
                        showSearch
                        optionFilterProp="label"
                        options={coachOptions}
                        value={splitCoaches}
                        onChange={setSplitCoaches}
                      />
                      <Button onClick={assignEvenly} disabled={!splitCoaches.length || !pending.length}>
                        Assign Evenly
                      </Button>
                    </Space>
                    <Space>
                      <Typography.Text>Status:</Typography.Text>
                      {/* Sets every selected card not yet created; each card can still be changed. */}
                      <Select
                        style={{ width: 110 }}
                        placeholder="Set all"
                        value={pending.length && pending.every((c) => c.status === pending[0].status) ? pending[0].status : undefined}
                        disabled={!pending.length}
                        onChange={(v) => {
                          const ids = new Set(pending.map((c) => c.UserID));
                          setCards((prev) => prev.map((c) => (ids.has(c.UserID) ? { ...c, status: v } : c)));
                        }}
                        options={STATUS_OPTIONS}
                      />
                      <Button type="primary" onClick={createAll} loading={creatingAll} disabled={!pending.length}>
                        Create All
                      </Button>
                    </Space>
                  </div>

                  <Segmented
                    style={{ marginBottom: 12 }}
                    value={view}
                    onChange={setView}
                    options={[{ label: 'All Agents', value: 'all' }, { label: 'One at a Time', value: 'one' }]}
                  />

                  {!visible.length ? (
                    <Empty description="No agents selected. Tick them on the Agents tab." />
                  ) : view === 'all' ? (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      {visible.map(renderCard)}
                    </Space>
                  ) : (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <Card size="small" style={{ width: 260, flexShrink: 0, position: 'sticky', top: 12 }}
                        styles={{ body: { padding: 4, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' } }}>
                        {visible.map((c) => {
                          const on = c.UserID === focused?.UserID;
                          return (
                            <div
                              key={c.UserID}
                              role="button"
                              tabIndex={0}
                              onClick={() => setFocusId(c.UserID)}
                              onKeyDown={(e) => { if (e.key === 'Enter') setFocusId(c.UserID); }}
                              style={{
                                padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                                background: on ? 'var(--ant-color-primary-bg)' : undefined,
                                fontWeight: on ? 600 : 400,
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.AgentName}</span>
                                {c.createdId && <Tag color="green" style={{ marginInlineEnd: 0 }}>#{c.createdId}</Tag>}
                              </div>
                              <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                {c.EvalCount} eval{c.EvalCount === 1 ? '' : 's'}
                                {c.AvgQAScore != null && ` · ${Number(c.AvgQAScore).toFixed(1)}%`}
                                {!c.createdId && c.coachId && ` · ${coachName(c.coachId) || ''}`}
                              </Typography.Text>
                            </div>
                          );
                        })}
                      </Card>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Button icon={<LeftOutlined />} disabled={focusIdx <= 0}
                            onClick={() => setFocusId(visible[focusIdx - 1].UserID)}>Previous</Button>
                          <Typography.Text type="secondary">{visible.length ? focusIdx + 1 : 0} of {visible.length}</Typography.Text>
                          <Button disabled={focusIdx >= visible.length - 1}
                            onClick={() => setFocusId(visible[focusIdx + 1].UserID)}>Next <RightOutlined /></Button>
                        </div>
                        {focused && renderCard(focused)}
                      </div>
                    </div>
                  )}
                  </>
                ),
              },
            ]}
          />
        )}
      </Spin>
    </>
  );
}
